<style lang="scss" scoped>
  @import '../../../sass/variables';

  .show {
    background: #32373a;
  }

  .page-editable {
    position: relative;

    span {
      cursor: pointer;
      position: relative;
    }

    .editable-modal {
      min-width: 400px;
      color: $gray-dark;
      background-color: $white-gray-2;
      cursor: initial;
      margin-top: 20px;
      display: flex;
      flex-direction: column;
      position: absolute;
      left: -40px;
      z-index: 10;
      transition: .3s;
      border-radius: 15px;
      box-shadow: 0 10px 20px rgba(103, 70, 162, 0.18);
      -webkit-text-stroke: thin;

      &::before {
        width: 30px;
        height: 15px;
        background-color: $white-gray-2;
        margin-top: -13px;
        content: '';
        position: absolute;
        right: 80%;
        transform: translateX(50%);
        clip-path: polygon(50% 0, 0 100%, 100% 100%);
      }

      .editable-header {
        font-size: 0.875rem;
        font-weight: 700;
        line-height: 1.375rem;
        letter-spacing: -0.21px;
        padding: 20px 20px 12px;
        margin: 0;
        display: flex;
        align-items: center;
      }

      .editable-body {
        padding: 0 20px;

        .tabs {
          display: flex;
          border-bottom: 1px solid $gray;

          .tab-link,
          ::v-deep .tab-link {
            min-width: 30px;
            min-height: 30px;
            display: flex;
            align-items: center;
            border-radius: 8px;
            transition: .3s;

            &.active {
              background-color: $gray-white-2;

              svg:not(.active) {
                display: none;
              }

              .active {
                display: block !important;
              }
            }

            &:hover {
              background-color: $gray-white-2;
            }
          }
        }

        .options {
          max-height: 200px;
          overflow-y: overlay;

          &::-webkit-scrollbar {
            width: 4px;
            border-radius: 2px;
          }

          &::-webkit-scrollbar-thumb {
            background: $gray-soft-2;
            border-radius: 2px;
          }
        }

        .form-group {
          text-align: left;
          margin-bottom: .5rem;

          .form-control {
            background: $white;
            padding: 0;
          }

          label {
            font-size: 0.875rem;
            line-height: 1.375rem;
            letter-spacing: -0.21px;
          }
        }
      }

      .editable-footer {
        width: 100%;
        padding: 20px;
      }
    }
  }
</style>

<template>
  <div
    class="page-editable"
    :class="{ show: show }"
    @click.prevent="show = ! show"
    v-if="tabs.length != 0"
  >
    <slot />
    <Transition name="editable-modal">
      <div
        class="editable-modal"
        v-show="show"
        @click.stop=""
      >
        <h3
          class="editable-header"
          v-if="loading"
        >
          <span><i class="fa fa-circle-o-notch fa-spin" /> {{ __('Carregando...') }}</span>
        </h3>
        <h3
          class="editable-header"
          v-else
        >
          {{ __('Opções de estilo') }} - {{ __(active.title) }}<a
            class="ml-auto"
            href="#"
            v-b-tooltip.bottomleft="__('Resetar configurações')"
            @click.stop="reset"
          >
            <IconRefresh
              class="mt-n1px"
              color="#3B334A"
            /></a>
        </h3>
        <div
          class="editable-body"
          v-if="! loading"
        >
          <div class="tabs pb-3 mb-3">
            <a
              class="tab-link mr-2"
              href="#"
              :class="{ active: tab.title == active.title }"
              v-b-tooltip.bottomright="tab.title"
              @click.stop.prevent="toggle(tab)"
              v-for="(tab, index) in tabs"
              :key="index"
            >
              <Component
                class="mx-auto"
                color="#B2A8C1"
                :is="iconComponents[tab.icon] || 'IconDashboard'"
              />
              <Component
                class="active d-none mx-auto"
                color="#3B334A"
                :is="iconComponents[tab.icon] || 'IconDashboard'"
              /></a>
            <PageCssEditor
              v-b-tooltip.bottomright="__('Editor de CSS')"
              :css="css"
              :url="url"
            />
          </div>
          <div class="options">
            <div
              class="form-group"
              v-for="(field, property) in active.fields"
              :key="property"
            >
              <label
                :for="property"
                v-if="label"
              >{{ field.label }}</label>
              <Component
                :is="components[field.type]"
                :placeholder="field.placeholder"
                :value="getValue(fields[property], field.default)"
                @input="(val) => updateField(property, val)"
              />
            </div>
          </div>
        </div>
        <div
          class="editable-footer"
          v-if="! loading"
        >
          <div class="row text-center">
            <div class="col-12">
              <button
                class="btn btn-primary f-w-500"
                type="button"
                @click.stop.prevent="save()"
              >
                {{ __('Salvar') }}
              </button>
            </div>
          </div>
          <div class="row text-center">
            <div class="col-12">
              <button
                class="btn btn-cancel f-w-500"
                type="button"
                @click.stop.prevent="show = false"
              >
                <u>{{ __('Cancelar') }}</u>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import { VBTooltip } from 'bootstrap-vue';
import Swal from 'sweetalert2';

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },

  props: {
    label: {
      type: Boolean,
      default: false,
    },
    theme: {
      type: String,
      default: '',
    },
    value: {
      type: Object,
      default: () => {},
    },
    url: {
      type: String,
      default: '',
    },
    css: {
      type: String,
      default: '',
    },
  },

  data () {
    return {
      show: false,
      loading: false,
      tabs: {},
      active: [],
      fields: this.value,
      components: {
        file: 'input-file',
        text: 'input-text',
        color: 'input-color',
      },
      iconComponents: {
        'fa fa-wpforms': 'IconBrowser',
        'fa fa-file-o': 'IconBlankFile',
        'fa fa-image': 'IconStackedBoxes',
        'fa fa-window-minimize': 'IconWindowMinimize',
      },
    };
  },

  mounted () {
    this.loadTabs(this.theme, false);

    Bus.$on('onThemeClick', () => this.show = false);
    // Bus.$on('themeUpdated', ({ theme, reset }) => this.loadTabs(theme, reset));
  },

  methods: {
    save (show = false, text = 'Estilo da landing page salvo.', cb = () => {}) {
      const clean = this.clean(this.fields);

      this.$http.put(this.url, { editable: clean }).then(() => {
        Alert.success(text);

        this.show = show;

        Bus.$emit('styleUpdated', false);
        cb();
      });
    },

    toggle (tab) {
      this.active = tab;
    },

    reset () {
      Swal.fire({
        title: this.__('Você deseja resetar o estilo da Landing Page?'),
        width: 350,
        buttonsStyling: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        confirmButtonColor: 'transparent',
        confirmButtonText: this.__('Resetar'),
        cancelButtonColor: 'transparent',
        cancelButtonText: this.__('Cancelar'),
        showCancelButton: true,
        customClass: {
          container: 'container-swal2',
          confirmButton: 'btn btn-primary f-w-500',
          cancelButton: 'btn btn-cancel f-w-500',
        },
      }).then((result) => {
        if (result.dismiss !== 'cancel') {
          this._defaults();
        }
      });
    },

    _defaults (show = true) {
      _.forEach(this.tabs, (tab) => {
        _.forEach(tab.fields, (field, property) => {
          this.fields[property] = field.default;
        });
      });

      this.save(show, 'Estilo da página resetado.', () => {
        this.$forceUpdate();
      });
    },

    clean (fields) {
      const cleaned = {};

      _.forEach(this.tabs, ({ fields: tabFields }) => {
        _.forEach(fields, (item, property) => {
          if (item && item.length && tabFields[property]) {
            cleaned[property] = item;
          }
        });
      });

      return cleaned;
    },

    loadTabs (theme, resetFields = true) {
      this.loading = true;

      this.$http.get(`/page/${theme}/tabs`).then(({ data: tabs }) => {
        this.tabs = tabs;
        this.toggle(tabs.general);
        this.loading = false;

        if (resetFields) {
          this._defaults(false);
        }
      })
        .catch((error) => {
          console.log(error);
          this.loading = false;
        });
    },

    updateField (property, value) {
      this.fields[property] = value;

      // if (field.attr && field.selector) {
      //  Bus.$emit('styleUpdated', [field.attr, value, field.selector]);
      // }
    },

    getValue (field, fieldDefault) {
      return field || fieldDefault;
    },
  },
};
</script>
